'use client';

import React, { useState, useCallback, useEffect } from 'react';
import { X } from 'lucide-react';
import { safeSetItem, STORAGE_KEYS } from '@/utils/storage/safeStorage';

interface EmailCollectorProps {
  source: string;
  title?: string;
  description?: string;
  buttonText?: string;
  onSuccess?: () => void;
  onClose?: () => void;
  showAsModal?: boolean;
}

declare global {
  interface Window {
    turnstile: {
      render: (element: string | HTMLElement, options: any) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

export default function EmailCollector({
  source,
  title = "Stay Updated!",
  description = "Get notified about new features and special offers.",
  buttonText = "Subscribe",
  onSuccess,
  onClose,
  showAsModal = false
}: EmailCollectorProps) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileWidgetId, setTurnstileWidgetId] = useState<string | null>(null);
  const [turnstileLoaded, setTurnstileLoaded] = useState(false);
  const [turnstileError, setTurnstileError] = useState(false);
  const [showManualVerify, setShowManualVerify] = useState(false);

  // Handle ESC key press
  useEffect(() => {
    if (!showAsModal || !onClose) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showAsModal, onClose]);

  // Load Turnstile script
  useEffect(() => {
    // Check if site key exists
    if (!process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY) {
      console.warn('Turnstile site key not configured');
      setTurnstileError(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      setTurnstileLoaded(true);
    };
    
    script.onerror = () => {
      console.error('Failed to load Turnstile script');
      setTurnstileError(true);
    };
    
    document.body.appendChild(script);

    return () => {
      if (turnstileWidgetId && window.turnstile) {
        try {
          window.turnstile.remove(turnstileWidgetId);
        } catch (e) {
          console.error('Error removing Turnstile widget:', e);
        }
      }
      document.body.removeChild(script);
    };
  }, []);

  // Initialize Turnstile widget with mobile/PWA considerations
  useEffect(() => {
    if (!turnstileLoaded || turnstileError) return;

    const initTurnstile = () => {
      // Check if we're in a PWA context
      const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                    (window.navigator as any).standalone === true;
      
      if (isPWA) {
        console.log('Running in PWA mode');
      }

      // Check if Turnstile is available
      if (!window.turnstile) {
        console.warn('Turnstile object not available yet');
        // Retry after a longer delay for mobile
        setTimeout(initTurnstile, 2000);
        return;
      }

      // Check if widget already exists
      const widgetElement = document.getElementById('turnstile-widget');
      if (!widgetElement || widgetElement.hasChildNodes()) {
        return;
      }

      try {
        console.log('Initializing Turnstile with site key:', process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY?.substring(0, 10) + '...');
        
        // Use compact size on mobile
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        
        const widgetId = window.turnstile.render('#turnstile-widget', {
          sitekey: process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY!,
          callback: (token: string) => {
            console.log('Turnstile verification successful');
            setTurnstileToken(token);
            setError('');
            // Store human verification permanently
            safeSetItem(STORAGE_KEYS.HUMAN_VERIFIED, 'true');
          },
          'error-callback': () => {
            console.error('Turnstile verification failed');
            setError('Verification failed. Please try again.');
            setTurnstileError(true);
          },
          'expired-callback': () => {
            console.log('Turnstile token expired');
            setTurnstileToken('');
          },
          size: isMobile ? 'compact' : 'normal',
          theme: 'light',
          retry: 'never', // Disable automatic retry
          'refresh-expired': 'never', // Disable automatic refresh when expired
          appearance: 'always' // Force visible challenge on mobile
        });

        setTurnstileWidgetId(widgetId);
        console.log('Turnstile widget initialized successfully');
      } catch (e) {
        console.error('Error initializing Turnstile:', e);
        setTurnstileError(true);
      }
    };

    // Use longer delay for mobile/PWA environments
    const timer = setTimeout(initTurnstile, 500);
    return () => clearTimeout(timer);
  }, [turnstileLoaded]);

  // Show manual verify button after delay on mobile
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!turnstileToken && !turnstileError) {
        setShowManualVerify(true);
      }
    }, 5000); // Show after 5 seconds if no token

    return () => clearTimeout(timer);
  }, [turnstileToken, turnstileError]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    if (!turnstileToken && !turnstileError) {
      setError('Please complete the verification');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch('/api/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          source,
          turnstileToken: turnstileToken || 'error-loading-turnstile'
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to subscribe');
      }

      setSuccess(true);
      setEmail('');
      
      // Store that email was collected permanently
      safeSetItem(STORAGE_KEYS.EMAIL_COLLECTED, 'true');
      
      // Reset Turnstile
      if (turnstileWidgetId && window.turnstile) {
        window.turnstile.reset(turnstileWidgetId);
      }
      
      if (onSuccess) {
        setTimeout(onSuccess, 2000);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [email, source, turnstileToken, turnstileWidgetId, onSuccess]);

  const content = (
    <div className={`${showAsModal ? 'bg-white rounded-lg shadow-xl p-6 max-w-md w-full relative' : ''}`}>
      {showAsModal && onClose && (
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
          aria-label="Close"
        >
          <X className="w-6 h-6" />
        </button>
      )}

      <h3 className="text-xl font-semibold mb-2">{title}</h3>
      <p className="text-gray-600 mb-4">{description}</p>

      {success ? (
        <div className="bg-green-50 text-green-800 p-4 rounded-md">
          ✓ Thank you for subscribing! We'll keep you updated.
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 placeholder-gray-500"
              disabled={loading}
            />
          </div>

          {error && (
            <div className="text-red-600 text-sm">{error}</div>
          )}

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-2 px-4 rounded-md font-medium transition-colors ${
              loading
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-purple-600 hover:bg-purple-700 text-white'
            }`}
          >
            {loading ? 'Subscribing...' : buttonText}
          </button>

          {/* Turnstile widget - moved below the button */}
          {!turnstileError && (
            <>
              <div className="text-xs text-gray-500 text-center">
                Please verify you're human
              </div>
              <div id="turnstile-widget" className="flex justify-center"></div>
            </>
          )}
          
          {turnstileError && (
            <div className="text-amber-600 text-sm text-center">
              ⚠️ Human verification is temporarily unavailable
            </div>
          )}
          
          {/* Manual verification option for mobile */}
          {showManualVerify && !turnstileToken && !turnstileError && (
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-2">Having trouble with verification?</p>
              <button
                type="button"
                onClick={() => {
                  // Try to reinitialize Turnstile
                  window.location.reload();
                }}
                className="text-purple-600 hover:text-purple-700 underline text-sm"
              >
                Reload verification
              </button>
            </div>
          )}

          {showAsModal && onClose && (
            <button
              type="button"
              onClick={onClose}
              className="w-full text-center text-sm text-gray-500 hover:text-gray-700 underline mt-2"
            >
              Skip for now
            </button>
          )}

          <p className="text-xs text-gray-500 text-center">
            By subscribing, you agree to receive emails from us. 
            You can unsubscribe at any time.
          </p>
        </form>
      )}
    </div>
  );

  if (showAsModal) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        {content}
      </div>
    );
  }

  return content;
}
